import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Mapping from "../../../../Common/Mapping/Mapping";
import { ButtonUI } from "../../../../Common";

import { showToast } from "../../../../../store/actions";
import { identifyDataTypeAndFormat, setPrevState } from "../../../../../util/common";
import { checkReservedKeywords } from "../../../../../util/restrictedStatement";

const HandleFields = (field) => {
  // Replace space with underscore(_) if field name contains space( )
  field = field.trim().replaceAll(/[^a-zA-Z0-9_$]/gi, "_");

  // Replace first chart with underscore(_) If field Name is not starting with Alphabet or underscore
  const regEx = /^[a-zA-Z_].*/;
  if (!regEx.test(field)) field = `_${field}`;

  return field;
};

const MappingPopup = ({ getS3FileColumns, setShowMapping, handleSave }) => {
  const attributeDataTypes = useSelector((state) => state.config.attributeDataTypes);

  const { options, formats, types } = useMemo(() => {
    const options = {};
    const formats = {};
    const types = [];

    attributeDataTypes.forEach((option) => {
      if (option.options) {
        if (option.type !== "Date" && option.type !== "DateTime" && option.type !== "Time")
          options[option.type] = option.options;
        else formats[option.type] = option.options;
      }

      if (option.type !== "Reference")
        types.push({
          value: option.type,
          text: option.type,
        });
    });

    return { options, formats, types };
  }, [attributeDataTypes]);

  const dispatch = useDispatch();

  const [state, setState] = useState({
    validMapping: false,
    loading: false,
  });
  const [columns, setColumns] = useState({
    headers: [],
    mappings: [],
    data: [],
    fileName: "",
    error: false,
  });

  const [scrollToIndex, setScrollToIndex] = useState(null);

  const getFileData = useCallback(async () => {
    getS3FileColumns(false).then((response) => {
      if (response.success) {
        let columnsDataTypesAndFormat = {};
        if (response.data.sampleData.length > 0) {
          columnsDataTypesAndFormat = identifyDataTypeAndFormat(response.data.fields, response.data.sampleData);
        } else {
          if (response.data.snowflakeFields) {
            response.data.snowflakeFields?.forEach(({ name, size, type: dataType }) => {
              columnsDataTypesAndFormat[name] = {
                dataType,
                size,
                format: "",
              };
            });
          } else {
            response.data.fields?.forEach((field) => {
              columnsDataTypesAndFormat[field] = {
                dataType: "String",
                format: null,
              };
            });
          }
        }
        setColumns({
          fileName: response?.data?.fileName || "",
          headers: response.data.fields,
          mappings: response.data.fields.map((field, i) => {
            const dataType = columnsDataTypesAndFormat[field]["dataType"];
            let format = "";
            let customFormat = "";
            if (formats[dataType] !== undefined) {
              let recivedFormat = columnsDataTypesAndFormat[field]["format"];
              if (recivedFormat !== undefined && recivedFormat !== null) {
                format = formats[dataType].find((el) => el.value === columnsDataTypesAndFormat[field]["format"]);
                if (format === undefined) {
                  format = { value: "Custom" };
                  customFormat = columnsDataTypesAndFormat[field]["format"];
                }
              }
            }
            let destinationName = HandleFields(field);

            let hasError = false;
            let errorMessage = "";

            if (checkReservedKeywords(destinationName)) {
              hasError = true;
              errorMessage = "Reserved keyword";
            } else if (destinationName.length > 128) {
              hasError = true;
              errorMessage = "Max limit: 128 Characters!";
            }

            let size = columnsDataTypesAndFormat[field]["length"];
            if (!size) {
              const currOptions = options[dataType];
              if (currOptions) {
                const index = dataType === "Number" ? 0 : dataType === "String" ? 5 : currOptions.length - 1;
                size = currOptions[index].value;
              } else size = "";
            }

            return {
              source: field,
              destination: destinationName,
              type: dataType,
              size,
              format: format ? format.value : "",
              customFormat: format && format.value === "Custom" ? customFormat : "",
              hasError,
              errorMessage,
              isSelected: false,
            };
          }),
          data: response.data.sampleData,
          error: response?.data?.error || false,
        });

        setPrevState(setState, { validMapping: true });
      } else {
        setPrevState(setState, { validMapping: false, loading: false });
        setShowMapping(false);
      }
    });
  }, [formats, getS3FileColumns, options, setShowMapping]);

  const updateMapping = useCallback(
    (value, source) => {
      if (value["destination"]) value["destination"] = value["destination"].replaceAll(" ", "");

      setColumns((prev) => {
        return {
          ...prev,
          mappings: prev.mappings.map((_mapping) =>
            _mapping.source === source
              ? {
                  ..._mapping,
                  ...value,
                  ...(value.type && _mapping.type !== value.type
                    ? {
                        size: options[value.type]
                          ? options[value.type][
                              value.type === "Number" ? 0 : value.type === "String" ? 5 : options[value.type].length - 1
                            ].value
                          : "",
                        format: formats[value.type] ? formats[value.type][formats[value.type].length - 1].value : "",
                        customFormat: "",
                      }
                    : {}),
                }
              : _mapping
          ),
        };
      });
    },
    [formats, options]
  );

  useEffect(() => {
    (async () => {
      getFileData();
    })();

    return () => {
      setState({
        validMapping: false,
        loading: false,
      });
      setShowMapping(false);
      setColumns({
        headers: [],
        mappings: [],
        data: [],
        fileName: "",
        error: false,
      });
    };
  }, [getFileData, setShowMapping]);

  //When Preview popup is opened then auto scroll to first reserved keyword if found
  useEffect(() => {
    if (state.validMapping) {
      let scrollIndex = null;
      columns.mappings.forEach(({ hasError }, index) => {
        if (hasError) scrollIndex = index;
      });
      if (scrollIndex) setScrollToIndex(scrollIndex);
    }

    //For first time only so not adding dependency for columns
  }, [state.validMapping]);

  //Validate Destination Columns string which allows alphanumeric, underscore and doller
  const validateDestinationCols = useCallback(
    (colMappings) => {
      let valid = false;
      //Find all reserved keywords and auto scroll to first reserved keyword if found
      let scrollIndex = null;
      let reservedFlags = columns.mappings.filter(({ source, destination, hasError }, index) => {
        const isReserved = checkReservedKeywords(destination);
        if (isReserved && scrollIndex === null) scrollIndex = index;
        if (isReserved !== hasError)
          updateMapping({ hasError: isReserved, errorMessage: isReserved ? "Reserved keyword" : "" }, source);
        return isReserved;
      });
      if (scrollIndex) setScrollToIndex(scrollIndex);

      //Validating unique columns
      const uniqueColumns = new Set(colMappings.map(({ destination }) => destination.toLowerCase()));
      if (colMappings.length !== uniqueColumns.size)
        dispatch(
          showToast({
            message: "Destination columns are not unique!",
            type: "error",
          })
        );
      else if (colMappings.some((column) => !column.destination.trim()))
        dispatch(
          showToast({
            message: "Destination columns can not be empty!",
            type: "error",
          })
        );
      else if (reservedFlags.length)
        dispatch(
          showToast({
            message: `${reservedFlags.length} destination column${
              reservedFlags.length > 1 ? "s" : ""
            } contain reserved keyword${reservedFlags.length > 1 ? "s" : ""}!`,
            type: "error",
          })
        );
      else if (colMappings.some((column) => column.destination.length > 128)) {
        let scrollIndex = 0;
        setColumns((prev) => {
          return {
            ...prev,
            mappings: prev.mappings.map((mapping, i) => {
              const hasError = mapping.destination.length > 128;
              if (!scrollIndex && hasError) {
                scrollIndex = i;
                setScrollToIndex(scrollIndex);
              }

              return mapping.destination.length > 128
                ? { ...mapping, hasError: true, errorMessage: "Max limit: 128 Characters!" }
                : mapping;
            }),
          };
        });
        dispatch(
          showToast({
            message: "Length of the Destination Column should be less than 128!",
            type: "error",
          })
        );
      } else if (
        columns.mappings.some(
          (column) =>
            column.format &&
            formats[column.type] &&
            column.format === "Custom" &&
            (column.customFormat === "" || column.customFormat === null || column.customFormat === undefined)
        )
      ) {
        dispatch(
          showToast({
            message: "Custom format cant't be empty, please provide custom format",
            type: "error",
          })
        );
      } else valid = true;

      return valid;
    },
    [columns.mappings, dispatch, formats, updateMapping]
  );

  const handleSubmit = useCallback(async () => {
    if (!validateDestinationCols(columns.mappings)) return;
    else {
      setShowMapping(false);
      handleSave(columns.mappings);
    }
  }, [columns.mappings, handleSave, setShowMapping, validateDestinationCols]);

  return (
    <Mapping
      handleClose={() => {
        setShowMapping(false);
      }}
      validMapping={state.validMapping}
      dataTypes={types}
      updateMapping={updateMapping}
      loading={state.loading}
      append={false}
      options={options}
      columns={columns}
      setColumns={setColumns}
      scrollToIndex={scrollToIndex}
      formats={formats}
      btnGroup={
        <ButtonUI
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleSubmit()}
          variant="contained"
          color="success"
          style={{ marginRight: 15 }}
          disabled={state.loading}
        >
          Import Attributes
        </ButtonUI>
      }
    />
  );
};

export default React.memo(MappingPopup);
